import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAuth, Auth, GoogleAuthProvider, signInWithPopup, signOut, User as FirebaseUser } from 'firebase/auth';
import { getFirestore, Firestore, doc, getDoc, setDoc, deleteDoc, collection, getDocs, updateDoc, Timestamp, getDocFromServer } from 'firebase/firestore';
import { getStorage, FirebaseStorage, ref, getDownloadURL, uploadString } from "firebase/storage";
import { SpaceThing, Todo, CalendarEvent, Layouts, DashboardConfig } from './types';

class FirebaseService {
  public app: FirebaseApp;
  public auth: Auth;
  public db: Firestore;
  public storage: FirebaseStorage;

  constructor() {
    const firebaseConfig = {
      apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
      storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
      appId: process.env.REACT_APP_FIREBASE_APP_ID
    };

    this.app = initializeApp(firebaseConfig);
    this.auth = getAuth(this.app);
    this.db = getFirestore(this.app, 'datatest');
    this.storage = getStorage(this.app);

    console.log('Firebase initialized successfully');
  }

  async signInWithGoogle(): Promise<FirebaseUser | null> {
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(this.auth, provider);
      return result.user;
    } catch (error) {
      console.error("Authentication error:", error);
      return null;
    }
  }

  async signOut(): Promise<void> {
    try {
      await signOut(this.auth);
    } catch (error) {
      console.error("Sign out error:", error);
    }
  }

  async isUserWhitelisted(email: string): Promise<boolean> {
    if (!email) return false;
  
    try {
      const whitelistDoc = await getDoc(doc(this.db, "whitelist", email));
      return whitelistDoc.exists();
    } catch (error: any) {
      const errorMessage = error?.message || '';
      console.error("Error checking whitelist:", {
        message: errorMessage,
        code: error?.code,
        name: error?.name
      });
      
      return false;
    }
  }

  async saveSpaceThing(thing: Partial<SpaceThing> & { id: string }): Promise<void> {
    // "Things" are now global, but for now we'll allow any authenticated user to add them.
    // In a real app, you'd add role-based admin permissions here.
    const user = this.auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    await setDoc(doc(this.db, 'things', thing.id), thing, { merge: true });
  }
  
  async getSpaceThings(): Promise<SpaceThing[]> {
    // "Things" are now global and readable by any authenticated user.
    const user = this.auth.currentUser;
    if (!user) return [];
    const querySnapshot = await getDocs(collection(this.db, 'things'));
    return querySnapshot.docs.map(doc => doc.data() as SpaceThing);
  }
  
  async deleteSpaceThing(id: string): Promise<void> {
    // This is now a global "thing". We should restrict deletion to admins.
    // For now, we will prevent deletion from the client-side for safety.
    console.warn("Deletion of global 'things' is restricted.");
    await deleteDoc(doc(this.db, 'things', id));
  }
  
  async saveTodo(todo: Todo): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) return;
    await setDoc(doc(this.db, 'users', user.uid, 'todos', todo.id), { ...todo, createdAt: Timestamp.fromDate(todo.createdAt) });
  }
  
  async getTodos(): Promise<Todo[]> {
    const user = this.auth.currentUser;
    if (!user) return [];
    const querySnapshot = await getDocs(collection(this.db, 'users', user.uid, 'todos'));
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return { ...data, id: doc.id, createdAt: (data.createdAt as Timestamp).toDate() } as Todo;
    });
  }
  
  async updateTodo(id: string, updates: Partial<Todo>): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) return;
    await updateDoc(doc(this.db, 'users', user.uid, 'todos', id), updates);
  }
  
  async deleteTodo(id: string): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) return;
    await deleteDoc(doc(this.db, 'users', user.uid, 'todos', id));
  }
  
  async saveEvent(event: CalendarEvent): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) return;
    await setDoc(doc(this.db, 'users', user.uid, 'events', event.id), { ...event, date: Timestamp.fromDate(event.date) });
  }
  
  async getEvents(): Promise<CalendarEvent[]> {
    const user = this.auth.currentUser;
    if (!user) return [];
    const querySnapshot = await getDocs(collection(this.db, 'users', user.uid, 'events'));
    return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return { ...data, id: doc.id, date: (data.date as Timestamp).toDate() } as CalendarEvent;
    });
  }
  
  async uploadImage(dataUrl: string, path: string): Promise<string> {
    // DIAGNOSTIC: Log upload attempt
    
    // Get and log the ID token to see what's in the token
    if (this.auth.currentUser) {
      try {
        const idTokenResult = await this.auth.currentUser.getIdTokenResult();
      } catch (err) {
        console.error('Error getting ID token:', err);
      }
    }
    
    // Check whitelist status in client
    if (this.auth.currentUser?.email) {
      try {
        const isWhitelisted = await this.isUserWhitelisted(this.auth.currentUser.email);
        
        // Also check the default database
        const defaultDbDoc = await getDoc(doc(getFirestore(this.app), "whitelist", this.auth.currentUser.email));
      } catch (err) {
        console.error('Error checking whitelist:', err);
      }
    }
    
    try {
      const storageRef = ref(this.storage, path);
      
      await uploadString(storageRef, dataUrl, 'data_url');
      
      const downloadUrl = await getDownloadURL(storageRef);
      
      return downloadUrl;
    } catch (error: any) {
      console.error('Upload failed with error:', {
        message: error.message,
        code: error.code,
        name: error.name,
        serverResponse: error.serverResponse,
        customData: error.customData
      });
      throw error;
    }
  }
  
  async deleteEvent(id: string): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) return;
    await deleteDoc(doc(this.db, 'users', user.uid, 'events', id));
  }
  
  async saveDashboardConfig(config: DashboardConfig): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) return;
    
    // Sanitize config to remove undefined values (Firebase doesn't allow them)
    const sanitizedConfig = this.removeUndefinedValues(config);
    
    await setDoc(doc(this.db, 'users', user.uid, 'dashboardConfig', 'config'), sanitizedConfig);
  }
  
  /**
   * Recursively removes undefined values from an object.
   * Firebase setDoc() doesn't accept undefined values - they must be null or omitted.
   */
  private removeUndefinedValues<T>(obj: T): T {
    if (obj === null || obj === undefined) {
      return obj;
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => this.removeUndefinedValues(item)) as T;
    }
    
    if (typeof obj === 'object') {
      const cleaned: any = {};
      for (const [key, value] of Object.entries(obj)) {
        if (value !== undefined) {
          cleaned[key] = this.removeUndefinedValues(value);
        }
      }
      return cleaned as T;
    }
    
    return obj;
  }
  
  async getDashboardConfig(): Promise<DashboardConfig | null> {
    const user = this.auth.currentUser;
    if (!user) return null;
    const docSnap = await getDoc(doc(this.db, 'users', user.uid, 'dashboardConfig', 'config'));
    if (docSnap.exists()) {
      return docSnap.data() as DashboardConfig;
    }
    return null;
  }
}

// Create and export a singleton instance
export const firebaseService = new FirebaseService();